import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiBookOpen, FiAlertTriangle, FiFileText, FiMap, FiExternalLink, FiCalendar, FiChevronDown, FiMapPin, FiPhone } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { applyPageSEO } from '../utils/seo';

const guides = [
  {
    title: "A Guide to the Driving Test",
    desc: "The official NSW Government PDF guide outlining all test requirements, maneuvers, and scoring criteria.",
    icon: FiFileText,
    link: "https://www.nsw.gov.au/sites/default/files/2021-08/guide-to-driving-test.pdf",
    color: "bg-blue-500",
    shadow: "shadow-blue-500/20"
  },
  {
    title: "Road Users' Handbook",
    desc: "Essential reading for all NSW drivers to fully understand road rules, signs, and safe driving behaviors.",
    icon: FiMap,
    link: "https://www.nsw.gov.au/sites/default/files/2022-11/Road-User-Handbook-English.pdf",
    color: "bg-brand-red",
    shadow: "shadow-brand-red/20"
  },
  {
    title: "Hazard Perception Handbook",
    desc: "Official handbook to help you prepare for the computer-based Hazard Perception Test (HPT).",
    icon: FiAlertTriangle,
    link: "https://www.nsw.gov.au/sites/default/files/2021-08/hazard-perception-handbook.pdf",
    color: "bg-brand-gold",
    shadow: "shadow-brand-gold/20"
  },
  {
    title: "Learner Licence Information",
    desc: "Detailed information on how to get your learner licence, including requirements and the DKT.",
    icon: FiBookOpen,
    link: "https://www.nsw.gov.au/driving-boating-and-transport/driver-and-rider-licences/driver-licences/learner-driver-licence",
    color: "bg-brand-green",
    shadow: "shadow-brand-green/20"
  },
  {
    title: "Book Your Driving Test",
    desc: "Direct access to the Service NSW portal to book your driving test or Hazard Perception Test online.",
    icon: FiCalendar,
    link: "https://www.myrta.com/wps/portal/extvp/myrta/licence/tbs/tbs-login/",
    color: "bg-brand-navy",
    shadow: "shadow-brand-navy/20"
  }
];

const categories = ['All', 'Instructor & Car', 'Lessons', 'Booking', 'Your Rights'];

const faqs = [
  {
    category: 'Instructor & Car',
    q: 'Who is the driving instructor at Drive Pal?',
    a: 'Santosh is the instructor and owner of Drive Pal Driving School. He has been teaching since 2021, holds a Certificate IV in Driving Instruction, and is licensed under the Driving Instructors Act 1992 (NSW). He is passionate about helping learners become safe and confident drivers through patient, step-by-step guidance.'
  },
  {
    category: 'Instructor & Car',
    q: 'What car will I learn to drive in?',
    a: 'Sessions are conducted exclusively in the student’s own registered and roadworthy automatic vehicle. Your vehicle must be registered, roadworthy, and clean. This allows you to practice and build confidence in the exact car you will use for your driving test. We do not provide a driving school car.'
  },
  {
    category: 'Instructor & Car',
    q: 'Do you offer manual driving lessons?',
    a: 'No. Drive Pal specialises exclusively in automatic transmission preparation. You must provide your own automatic vehicle for the session.'
  },
  {
    category: 'Lessons',
    q: 'What areas do you service?',
    a: 'Services are available within Goulburn town only. Santosh can meet you at your home, school, or another agreed location within Goulburn.',
    highlight: 'Outside Goulburn? Pick-up and drop-off are not available outside the Goulburn town area. Out-of-town students must meet near the Service NSW testing centre in Goulburn. Please phone or text us before making a booking: 0426 501 299'
  },
  {
    category: 'Lessons',
    q: 'How many lessons do I need before my driving test?',
    a: (
      <>
        <p className="mb-3">Every learner is different. Santosh assesses your skill level and builds a tailored plan to help you meet NSW driving test standards.</p>
        <p className="mb-3">NSW requires learners under 25 to complete a minimum of 120 hours of supervised practice (including 20 hours at night) before sitting the driving test. Learners aged 25 and over are exempt from the logbook requirement but still need to be competent to pass the practical test.</p>
        <p className="mb-3"><strong>Note:</strong> 1 hour of lessons with a licensed instructor counts as 3 logbook hours, up to a maximum of 10 professional lessons.</p>
        <p>Drive Pal does not guarantee a pass outcome — results depend on individual readiness and preparation.</p>
      </>
    )
  },
  {
    category: 'Lessons',
    q: 'Do you offer a pre-test or mock lesson?',
    a: 'Yes. A pre-test lesson is highly recommended, usually booked about a week before your test. It covers local intersections, speed zones, parking tasks and road conditions commonly encountered during driving assessments. Actual test routes vary.',
    notice: 'Completing a pre-test lesson does not guarantee a pass. It is designed to identify final areas for improvement and help you prepare confidently.'
  },
  {
    category: 'Booking',
    q: 'How do I book a session?',
    a: 'Book online at drivepal.au or contact Santosh directly: phone or text 0426 501 299 or email book.drivepal@gmail.com.',
    highlight: 'Outside Goulburn? Please phone or text before booking. You will need to meet near the Service NSW testing centre — no pick-up or drop-off is available outside the Goulburn town area.'
  },
  {
    category: 'Your Rights',
    q: 'How is my personal information handled?',
    a: (
      <>
        Drive Pal handles personal information in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles. Your data is only used to provide our services. You have the right to access or correct your information by contacting{' '}
        <a href="mailto:book.drivepal@gmail.com" className="text-brand-gold underline underline-offset-2 font-semibold hover:text-brand-navy transition-colors">book.drivepal@gmail.com</a>. Unresolved complaints can be referred to the{' '}
        <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className="text-brand-gold underline underline-offset-2 font-semibold hover:text-brand-navy transition-colors">Office of the Australian Information Commissioner</a>.
      </>
    )
  },
];

function FaqItem({ faq, isOpen, onToggle }) {
  return (
    <div className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen ? 'border-brand-gold shadow-lg' : 'border-gray-200 shadow-sm'} bg-white`}>
      <button 
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 md:p-6 text-left gap-4 hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <span className="font-display text-base md:text-lg text-brand-navy leading-snug">{faq.q}</span>
        <motion.span 
          animate={{ rotate: isOpen ? 180 : 0 }} 
          transition={{ duration: 0.25 }}
          className="shrink-0"
        >
          <SafeIcon icon={FiChevronDown} className="text-brand-gold text-xl" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-6 space-y-4 border-t border-gray-100 pt-4">
              <div className="text-gray-700 leading-relaxed">{faq.a}</div>
              
              {faq.notice && (
                <div className="flex items-start gap-3 bg-amber-50 border-l-4 border-amber-400 rounded-r-xl p-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-amber-500 text-lg shrink-0 mt-0.5" />
                  <p className="text-amber-800 text-sm font-medium leading-relaxed">{faq.notice}</p>
                </div>
              )}

              {faq.highlight && (
                <div className="flex items-start gap-3 bg-brand-green/10 border-l-4 border-brand-green rounded-r-xl p-4">
                  <SafeIcon icon={FiMapPin} className="text-brand-green text-lg shrink-0 mt-0.5" />
                  <p className="text-brand-navy/80 text-sm font-medium leading-relaxed">{faq.highlight}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function GuidesPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    applyPageSEO({
      title: 'Learner Driver Guides & Resources | Drive Pal Driving School Goulburn NSW',
      description: 'Free learner driver guides and resources for NSW. Access the official driving test guide, Road Users Handbook, Hazard Perception handbook, and FAQs about driving lessons in Goulburn with Drive Pal.',
      keywords: 'learner driver guide NSW, driving test guide Goulburn, road users handbook NSW, hazard perception test NSW, how to get learner licence NSW, driving lesson FAQ Goulburn, driving school resources Goulburn NSW, L plate guide NSW, P plate test preparation Goulburn, 120 logbook hours NSW, driving instructor FAQ Goulburn',
      url: 'https://drivepal.au/#/guides',
      ogTitle: 'Learner Driver Guides & FAQs | Drive Pal Driving School Goulburn',
      ogDescription: 'Official NSW driving resources, handbooks, and FAQs to help Goulburn learner drivers prepare for their driving test. Free guides from Drive Pal Driving School.',
    });
  }, []);

  const filtered = activeCategory === 'All' 
    ? faqs 
    : faqs.filter(f => f.category === activeCategory);

  const handleToggle = (idx) => setOpenFaq(prev => prev === idx ? null : idx);

  return (
    <div className="min-h-screen pt-20 bg-brand-offwhite">
      {/* Header */}
      <section className="bg-brand-navy py-16 px-4 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-brand-green/20 rounded-full blur-3xl -translate-y-1/2 -translate-x-1/2" />
        
        <div className="container mx-auto text-center relative z-10 max-w-3xl">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl md:text-5xl text-white mb-4"
          >
            Learner <span className="text-brand-gold">Guides & Resources</span>
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-white/80 text-lg md:text-xl font-medium"
          >
            Access official NSW resources, handbooks, and test information to help you prepare for your driving journey.
          </motion.p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {guides.map((guide, index) => (
              <motion.a
                key={index}
                href={guide.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className={`flex flex-col sm:flex-row items-start sm:items-center gap-6 bg-white p-8 rounded-2xl shadow-lg border border-gray-100 transition-all ${guide.shadow} hover:shadow-2xl group`}
              >
                <div className={`w-16 h-16 shrink-0 rounded-2xl ${guide.color} text-white flex items-center justify-center text-3xl shadow-inner group-hover:rotate-6 transition-transform duration-300`}>
                  <SafeIcon icon={guide.icon} />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-2xl text-brand-navy mb-2 group-hover:text-brand-gold transition-colors">{guide.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{guide.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-bold text-brand-navy uppercase tracking-wider">
                    View Resource <SafeIcon icon={FiExternalLink} className="text-brand-gold" />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 px-4 bg-white border-t border-gray-100">
        <div className="container mx-auto max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-display text-3xl md:text-4xl text-brand-navy mb-3">
              Frequently Asked <span className="text-brand-gold">Questions</span>
            </h2>
            <div className="w-16 h-1 bg-brand-gold mx-auto rounded-full" />
          </motion.div>

          {/* Categories */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-2 mb-10"
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setActiveCategory(cat);
                  setOpenFaq(null);
                }}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                  activeCategory === cat
                    ? 'bg-brand-navy text-white border-brand-navy shadow-md'
                    : 'bg-white text-brand-navy border-brand-navy/20 hover:border-brand-gold hover:text-brand-gold'
                }`}
              >
                {cat}
              </button>
            ))}
          </motion.div>

          {/* FAQ List */}
          <div className="space-y-3">
            <AnimatePresence mode="wait">
              {filtered.map((faq, idx) => (
                <motion.div
                  key={`${activeCategory}-${idx}`}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ delay: idx * 0.04 }}
                >
                  <FaqItem 
                    faq={faq} 
                    isOpen={openFaq === idx} 
                    onToggle={() => handleToggle(idx)} 
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 p-6 bg-brand-navy/5 rounded-2xl border border-brand-navy/10 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left"
          >
            <p className="text-gray-700 text-sm md:text-base">
              <strong className="text-brand-navy">Still have a question?</strong><br />
              Phone or text us and we'll get back to you promptly.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a 
                href="tel:0426501299" 
                className="inline-flex items-center gap-2 bg-brand-navy text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-brand-gold hover:text-brand-navy transition-colors whitespace-nowrap"
              >
                <SafeIcon icon={FiPhone} /> Phone or Text: 0426 501 299
              </a>
              <a 
                href="mailto:book.drivepal@gmail.com" 
                className="inline-flex items-center gap-2 bg-white border border-brand-navy/20 text-brand-navy px-5 py-2.5 rounded-full text-sm font-semibold hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
              >
                book.drivepal@gmail.com
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}